import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Bot, ChevronRight, ClipboardCheck, Home, MoreHorizontal, Route, Target, UserRound, UsersRound, X } from 'lucide-react'
import clsx from 'clsx'
import { useSidebarState } from '@/store/selectors'
import { useLearnerSnapshot } from '@/store/selectors/learner'
import { getSidebarProgressNavState } from './sidebarNavigation'
import './Sidebar.css'

const navItems = [
  { to: '/', label: '首页', icon: Home, end: true, mobile: true },
  { to: '/learning-path', label: '学习路径', icon: Route, end: false, mobile: true },
  { to: '/practice', label: '专项练习', icon: Target, end: false, mobile: true },
  { to: '/assessment', label: '能力检测', icon: ClipboardCheck, end: false, mobile: false },
  { to: '/friends', label: '学习伙伴', icon: UsersRound, end: false, mobile: false },
  { to: '/progress?mode=ai-teacher', label: 'AI 老师', icon: Bot, end: false, mobile: true },
  { to: '/me', label: '我的', icon: UserRound, end: false, mobile: false },
] as const

export default function Sidebar() {
  const student = useLearnerSnapshot()
  const { stars } = useSidebarState()
  const { pathname, search } = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)
  const progressNavState = getSidebarProgressNavState(pathname, search)
  const mobileMoreItems = navItems.filter((item) => !item.mobile)
  const isMoreActive = mobileMoreItems.some((item) => {
    if (item.label === '我的') return progressNavState.my
    return pathname.startsWith(item.to.split('?')[0])
  })

  useEffect(() => setMoreOpen(false), [pathname, search])

  return (
    <aside className="app-sidebar">
      <NavLink className="sidebar-brand" to="/" aria-label="OpenELP 首页">
        <span>E</span>
        <div><strong>OpenELP</strong><small>星辉学院</small></div>
      </NavLink>

      <NavLink className="sidebar-student" to="/me">
        <span className="sidebar-avatar">{student.avatar}</span>
        <div>
          <strong>{student.name}</strong>
          <small>{student.grade} · {student.layer ? `${student.layer} 层` : '待前测'}</small>
        </div>
        <span className="sidebar-stars">★ {stars}</span>
      </NavLink>

      <nav className="sidebar-nav" aria-label="主要导航">
        {navItems.map(({ to, label, icon: Icon, end, mobile }) => {
          const customActive = label === 'AI 老师'
            ? progressNavState.aiTeacher
            : label === '我的'
              ? progressNavState.my
              : null

          if (customActive !== null) {
            return (
              <Link
                key={to}
                to={to}
                className={clsx('sidebar-nav-item', customActive && 'active')}
                aria-current={customActive ? 'page' : undefined}
                data-mobile-hidden={!mobile}
              >
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </Link>
            )
          }

          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => clsx('sidebar-nav-item', isActive && 'active')}
              data-mobile-hidden={!mobile}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          )
        })}
        <button
          type="button"
          className={clsx('sidebar-nav-item sidebar-more-toggle', (isMoreActive || moreOpen) && 'active')}
          aria-expanded={moreOpen}
          aria-controls="sidebar-more-menu"
          onClick={() => setMoreOpen((open) => !open)}
        >
          <MoreHorizontal aria-hidden="true" />
          <span>更多</span>
        </button>
      </nav>

      {moreOpen && (
        <section className="sidebar-more-sheet" id="sidebar-more-menu" aria-label="更多学习入口">
          <header>
            <strong>更多入口</strong>
            <button type="button" onClick={() => setMoreOpen(false)} aria-label="关闭更多入口">
              <X aria-hidden="true" />
            </button>
          </header>
          <nav aria-label="更多导航">
            {mobileMoreItems.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} className="sidebar-more-link">
                <Icon aria-hidden="true" />
                <span>{label}</span>
                <ChevronRight aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </section>
      )}

      <footer className="sidebar-footer">
        <span>今日连续学习</span>
        <strong>{student.streak} 天</strong>
      </footer>
    </aside>
  )
}
